// De algemene weergave-opties van een widget (tabblad Weergave in het dashboard, golf 1A, Martijn 10-10-2026).
//
// De api geeft ze mee in widgetConfig.weergave (api/Widget/GetWidgetSettings): taal, logo, kop, starttekst, knopteksten,
// bevestiging, doorsturen na afloop, hoe ver vooruit en tot hoe kort van tevoren, verplichte velden en het voorwaardenvinkje.
// Leeg of null = de widget doet wat hij altijd deed. Hier de regels als losse functies, zodat ze te toetsen zijn
// (weergave.test.js); de schermen roepen ze aan.
import dayjs from './datum';

const tekst = (waarde) => (typeof waarde === 'string' && waarde.trim() ? waarde.trim() : null);

/** Een eigen tekst uit de weergave (kop, starttekst, startknop, boekknop, bevestiging, voorwaardentekst), of null. */
export function weergaveTekst(weergave, sleutel) {
    return tekst(weergave?.[sleutel]);
}

/**
 * De tekst van de knop op het eerste scherm. Eerst de eigen tekst van deze widget (tabblad Weergave, Knop om te beginnen), dan de
 * tekst van de zaak (Gastgroepen, Tekst van de reserveerknop) maar ALLEEN bij het restaurant, anders de standaard van de module.
 * De tekst van de zaak is een restauranttekst ("Tafel boeken"); die stond ook op de knop van de activiteiten- en salonwidget
 * (schermcontrole 11-10-2026, 990399).
 *
 * @param {object} weergave   widgetConfig.weergave
 * @param {string} module     "horeca", "activity", "salon" ...
 * @param {string} zaakknop   widgetSettings.buttonText
 * @param {string} standaard  de standaardtekst van de module (languageWords.Booknow)
 */
export function startknopTekst(weergave, module, zaakknop, standaard) {
    return weergaveTekst(weergave, "startknop")
        || (String(module || "").toLowerCase() === "horeca" ? tekst(zaakknop) : null)
        || standaard
        || null;
}

const hoofdcode = (code) => String(code || '').trim().split(/[-_]/)[0].toUpperCase();

/**
 * De taal van de widget.
 *  - weergave.taal leeg = de taal uit de insluitcode / het adres, zoals altijd;
 *  - weergave.taal "AUTO" = de taal van de browser als de zaak die heeft, anders de insluittaal;
 *  - een taalcode = die taal vast (als de zaak hem heeft; zonder talenlijst gewoon die taal).
 *
 * @param {object} weergave      widgetConfig.weergave
 * @param {Array}  languageData  de talen van de zaak ({ langcode })
 * @param {string} insluittaal   de taal uit de insluitcode / het adres
 * @param {string} navigatorTaal navigator.language
 * @returns {string} een taalcode zoals de zaak hem schrijft
 */
export function kiesTaal(weergave, languageData, insluittaal, navigatorTaal) {
    const talen = Array.isArray(languageData) ? languageData.filter((t) => t?.langcode) : [];
    const zoek = (code) => talen.find((t) => hoofdcode(t.langcode) === hoofdcode(code))?.langcode || null;

    const keuze = tekst(weergave?.taal);
    // Niets ingesteld: de taal uit de insluitcode / het adres, zoals altijd.
    if (!keuze) return insluittaal;

    // "AUTO": de taal van de browser als de zaak die heeft, anders de insluittaal.
    if (keuze.toUpperCase() === 'AUTO') {
        const browser = tekst(navigatorTaal);
        return (browser && zoek(browser)) || insluittaal;
    }

    // Een vaste taal.
    if (talen.length === 0) return keuze;
    return zoek(keuze) || insluittaal;
}

/** De laatste dag die de gast mag kiezen (dayjs, begin van de dag), of null zonder grens. */
export function laatsteDatum(weergave, vandaag = dayjs()) {
    const dagen = Number(weergave?.maxDagenVooruit);
    if (!Number.isFinite(dagen) || dagen <= 0) return null;
    return dayjs(vandaag).startOf('day').add(Math.floor(dagen), 'day');
}

/** Ligt deze datum verder vooruit dan de widget toestaat? */
export function datumTeVer(weergave, datum, vandaag = dayjs()) {
    const grens = laatsteDatum(weergave, vandaag);
    if (!grens || !datum) return false;
    const d = dayjs(datum);
    return d.isValid() && d.startOf('day').isAfter(grens);
}

/**
 * Kan dit moment nog geboekt worden met het laatste boekmoment (minuten van tevoren)?
 *
 * @param {object} weergave
 * @param {*} moment  een dayjs, Date of tekst met datum en tijd
 * @param {*} nu
 */
export function tijdNogBoekbaar(weergave, moment, nu = dayjs()) {
    const minuten = Number(weergave?.laatsteBoekmomentMinuten);
    if (!Number.isFinite(minuten) || minuten <= 0 || !moment) return true;
    const m = dayjs(moment);
    if (!m.isValid()) return true;
    return m.diff(dayjs(nu), 'minute', true) >= minuten;
}

/**
 * Hetzelfde voor een datum als "DD-MM-YYYY" of "YYYY-MM-DD" en een tijd als "HH:mm" of "HH:mm:ss".
 */
export function tijdOpDatumBoekbaar(weergave, datum, tijd, nu = dayjs()) {
    if (!datum || !tijd) return true;
    const t = String(tijd).trim().slice(0, 5);
    const dag = typeof datum === 'string' ? datum : (dayjs.isDayjs(datum) || datum instanceof Date ? dayjs(datum).format('YYYY-MM-DD') : null);
    if (!dag) return true;
    const d = dayjs(`${dag} ${t}`, ['DD-MM-YYYY HH:mm', 'YYYY-MM-DD HH:mm', 'DD/MM/YYYY HH:mm'], true);
    return tijdNogBoekbaar(weergave, d.isValid() ? d : null, nu);
}

/** Moet het gastveld met deze tag ({phone}, {address} ...) ingevuld worden? */
export function isVerplicht(weergave, tag) {
    const lijst = Array.isArray(weergave?.verplichteVelden) ? weergave.verplichteVelden : [];
    if (!tag || lijst.length === 0) return false;
    const t = String(tag).trim().toLowerCase();
    return lijst.some((v) => String(v).trim().toLowerCase() === t);
}

/**
 * De gastvelden met de verplichte velden uit de weergave erbij (field.required = true voor die tags). Werkt met een lijst
 * en met { data: [...] } (activiteiten). Zonder verplichte velden: precies wat erin ging.
 */
export function metVerplicht(velden, weergave) {
    const lijst = Array.isArray(weergave?.verplichteVelden) ? weergave.verplichteVelden : [];
    if (lijst.length === 0 || !velden) return velden;
    const zet = (rij) => rij.map((veld) => (veld && !veld.required && isVerplicht(weergave, veld.tag) ? { ...veld, required: true } : veld));
    if (Array.isArray(velden)) return zet(velden);
    if (Array.isArray(velden?.data)) return { ...velden, data: zet(velden.data) };
    return velden;
}

/** De naam van het voorwaardenvinkje in de formulieren (Formik). */
export const VOORWAARDENVELD = 'jmVoorwaarden';

/**
 * Het voorwaardenvinkje in de formulierregels: met voorwaardenTonen moet het aan staan.
 *
 * @param {object} weergave
 * @param {{initialValues: object, validationSchema: object}} uitkomst wat het formulier zelf al had (Yup-object)
 * @param {object} Yup      de Yup-module van het formulier
 * @param {string} melding  de foutmelding
 */
export function metVoorwaarden(weergave, uitkomst, Yup, melding) {
    if (!voorwaardenAan(weergave) || !uitkomst?.validationSchema || !Yup) return uitkomst;
    return {
        initialValues: { ...uitkomst.initialValues, [VOORWAARDENVELD]: false },
        validationSchema: uitkomst.validationSchema.shape({ [VOORWAARDENVELD]: Yup.boolean().oneOf([true], melding || 'Required') }),
    };
}

/** Het adres om na afloop naartoe te gaan (alleen http/https), of null. */
export function doorsturenAdres(weergave) {
    const adres = tekst(weergave?.doorsturenUrl);
    if (!adres) return null;
    try {
        const u = new URL(adres);
        return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
    } catch {
        return null;
    }
}

/** Het adres van de voorwaarden (alleen http/https), of null. */
export function voorwaardenAdres(weergave) {
    return doorsturenAdres({ doorsturenUrl: weergave?.voorwaardenUrl });
}

/** De herroepingspagina van de zaak ("Herroep overeenkomst hier", wetgeving punt 2), of null als de knop uit staat. */
export function herroepAdres(weergave) {
    return doorsturenAdres({ doorsturenUrl: weergave?.herroepUrl });
}

/** Moet de gast het voorwaardenvinkje zetten? */
export function voorwaardenAan(weergave) {
    return weergave?.voorwaardenTonen === true;
}

/** Het logo bovenin, of null. */
export function logoAdres(weergave) {
    return weergave?.logoTonen && tekst(weergave?.logo) ? tekst(weergave.logo) : null;
}

/**
 * Het live voorbeeld uit het dashboard: platte css-waarden ({ MainText: '#fff', MainText_fieldText: 'Hallo' }) in de
 * css-groepen van de store ([{ cssTabFieldsGroupName, fields: {...} }]). Een sleutel komt in de groep waar hij (of zijn
 * basis zonder _fieldText/_fontSize) in staat.
 */
export function voegCssSamen(groepen, plat) {
    if (!Array.isArray(groepen) || !plat || typeof plat !== 'object') return groepen;
    return groepen.map((groep) => {
        const velden = groep?.fields;
        if (!velden || typeof velden !== 'object') return groep;
        let nieuw = null;
        for (const [sleutel, waarde] of Object.entries(plat)) {
            const basis = sleutel.replace(/_(fieldText|fontSize)$/, '');
            if (sleutel in velden || basis in velden) {
                if (!nieuw) nieuw = { ...velden };
                nieuw[sleutel] = waarde;
            }
        }
        return nieuw ? { ...groep, fields: nieuw } : groep;
    });
}

/** De kleur van de kop of de starttekst uit het tabblad Weergave (kopKleur, starttekstKleur), of null = zoals het was. */
export function weergaveKleur(weergave, sleutel) {
    const waarde = typeof weergave?.[sleutel] === 'string' ? weergave[sleutel].trim() : '';
    return /^#[0-9a-f]{6}$/i.test(waarde) ? waarde : null;
}

/** Het live voorbeeld uit het dashboard: de weergave over die van de widget heen. */
export function voegWeergaveSamen(config, weergave) {
    if (!weergave || typeof weergave !== 'object') return config;
    return { ...(config || {}), weergave: { ...(config?.weergave || {}), ...weergave } };
}

/**
 * Het live voorbeeld uit Widget bewerken, stap Afbeelding (Martijn 11-10-2026): de afbeelding, de plek van het formulier, de hoogte
 * en de grenzen voor het aantal gasten over widgetConfig.settings heen. Alleen deze velden; de rest blijft van de server.
 */
export const VOORBEELDINSTELLINGEN = ['imageFileName', 'widgetPosition', 'imageHeight', 'isMinMaxGuestAvailable', 'minGuests', 'maxGuests'];

export function voegInstellingenSamen(config, instellingen) {
    if (!instellingen || typeof instellingen !== 'object') return config;
    const erbij = {};
    for (const sleutel of VOORBEELDINSTELLINGEN) {
        if (sleutel in instellingen) erbij[sleutel] = instellingen[sleutel];
    }
    if (!Object.keys(erbij).length) return config;
    return { ...(config || {}), settings: { ...(config?.settings || {}), ...erbij } };
}

/** Staat de widget in het live voorbeeld van het dashboard (?weergavevoorbeeld=1)? */
export function isVoorbeeld(zoek) {
    try {
        return new URLSearchParams(zoek || '').get('weergavevoorbeeld') === '1';
    } catch {
        return false;
    }
}

/** Is dit een voorbeeldbericht van het dashboard, van de pagina eromheen? */
export function isVoorbeeldBericht(gebeurtenis, ouder) {
    const data = gebeurtenis?.data;
    return !!data && typeof data === 'object' && gebeurtenis.source === ouder && ouder != null
        && data.bron === 'jimani-dashboard' && data.soort === 'voorbeeld';
}
